import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Flame, Tent, ArrowRight, Star, Eye, Lock } from "lucide-react";

/**
 * iter121 — Trending campsites in the last 24h.
 *
 * Polls `/api/campsites/trending?window=24h` (server-cached 60s) and
 * shows up to 6 cards. Auto-hides when there are no recent views so the
 * Landing page never renders an empty section. Pings every 60s so the
 * tile feels alive while a visitor sits on the homepage.
 */
export default function MostVisitedTile() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/campsites/trending?window=24h&limit=6");
        if (!cancelled) setItems(data?.items || []);
      } catch {
        /* silent — section auto-hides on error */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading || items.length === 0) return null;

  // iter189 — anonymous visitors see the "top 5" as a tantalising strip
  // with a signup CTA at the end. Signed-in members get the full grid.
  const anonView = !user;
  const list = anonView ? items.slice(0, 5) : items;

  return (
    <section className="border-b hairline bg-[#0e0e0e] text-white" data-testid="most-visited-tile">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="eyebrow text-[#ff4500] mb-2 inline-flex items-center gap-2">
              <Flame className="w-3 h-3" strokeWidth={2.4} />
              {anonView ? "Today's 5 most-viewed · free preview" : "Trending · last 24h"}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">
              {anonView ? "A taste of what's inside" : "Most visited sites today"}
            </h2>
            <p className="text-sm text-[#aaa] mt-2 max-w-xl">
              {anonView
                ? "300 rotating campsites this week (refreshed every Monday). Sign up free to unlock all 22,000+, save favourites and see full prices."
                : "Where other campers are actually looking right now — updated live, hourly-deduped."}
            </p>
          </div>
          <Link
            to={anonView ? "/register?next=%2Fbrowse" : "/browse"}
            className="inline-flex items-center gap-1 text-sm font-display text-[#ff4500] hover:underline"
            data-testid="most-visited-tile-see-all"
          >
            {anonView ? <>Sign up to unlock all 22k <ArrowRight className="w-4 h-4" strokeWidth={2} /></> : <>Browse all 22k <ArrowRight className="w-4 h-4" strokeWidth={2} /></>}
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((s, idx) => (
            <Link
              key={s.campsite_id}
              to={`/campsites/${s.campsite_id}`}
              className="group block bg-[#1a1a1a] hairline hover:border-[#ff4500] transition-colors"
              data-testid={`most-visited-tile-${s.campsite_id}`}
            >
              <div className="aspect-[4/3] bg-[#222] overflow-hidden relative">
                {s.image ? (
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40">
                    <Tent className="w-12 h-12" strokeWidth={1.5} />
                  </div>
                )}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-[#ff4500] text-white mono">
                  #{idx + 1} · Trending
                </span>
                {s.views_24h > 0 && (
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-black/70 backdrop-blur-sm text-white mono uppercase tracking-wider">
                    <Eye className="w-3 h-3" /> {s.views_24h}
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="font-display text-base leading-tight group-hover:text-[#ff4500] line-clamp-2">
                  {s.name}
                </div>
                <div className="text-xs mono text-[#999] mt-1 truncate">{s.region}</div>
                <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                  {typeof s.rating === "number" && s.rating > 0 && (
                    <span className="flex items-center gap-1 text-white/80">
                      <Star className="w-3 h-3 fill-[#ff4500] text-[#ff4500]" />
                      <span className="mono">{s.rating.toFixed(1)}</span>
                    </span>
                  )}
                  {s.is_direct_booking && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#fff4ee] text-[#7a2400] border border-[#ff4500]/40 mono">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#ff4500]"></span> Book direct
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {anonView && (
          <div className="mt-8 text-center" data-testid="most-visited-tile-anon-cta">
            <p className="text-sm text-white/80 mb-3">
              These are today's top 5 — 300 more rotate every Monday for guests. <strong className="text-white">Sign up free to unlock all 22,000+</strong>, full prices, live map, and save your favourites.
            </p>
            <Link
              to="/register?next=%2Fbrowse"
              className="inline-flex items-center gap-2 bg-[#ff4500] text-white font-black text-xs uppercase tracking-widest px-5 py-3 shadow-[0_4px_0_0_#5a1500] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#5a1500] transition-transform"
              data-testid="most-visited-tile-anon-signup"
            >
              <Lock className="w-3 h-3" /> Unlock the full list
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
